import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout';
import { Button } from '@/components/ui';
import { useAuth } from '@/features/auth';
import { useEffectiveBusinessId } from '@/hooks';
import { useBusinessContextStore } from '@/features/business-context';
import { useProjects } from '../hooks/useProjects';
import { useCustomers } from '../hooks/useCustomers';
import { ProjectCard } from '../components/ProjectCard';
import { ProjectFilters } from '../components/ProjectFilters';

export function ProjectsListPage() {
  const { profile } = useAuth();
  const effectiveBusinessId = useEffectiveBusinessId();
  const selectedBusinessName = useBusinessContextStore(
    (state) => state.selectedBusinessName
  );

  const isSuperAdmin = profile?.role === 'super_admin';

  // Fetch projects
  const {
    projects,
    isLoading,
    error,
    filters,
    sorting,
    setFilters,
    setSorting,
    clearFilters,
  } = useProjects({
    businessId: effectiveBusinessId,
    autoFetch: true,
  });

  // Fetch customers for filter dropdown
  const { customers } = useCustomers({
    businessId: effectiveBusinessId,
    autoFetch: true,
  });

  // Check if business is selected (for super admins)
  const showBusinessWarning = isSuperAdmin && !effectiveBusinessId;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">Projects</h1>
            <p className="text-secondary-600 mt-1">
              Manage your renovation projects
              {isSuperAdmin && selectedBusinessName && (
                <span className="text-primary-600"> • {selectedBusinessName}</span>
              )}
            </p>
          </div>
          <Link to="/projects/create">
            <Button variant="primary" disabled={showBusinessWarning}>
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              New Project
            </Button>
          </Link>
        </div>

        {/* Business Warning for Super Admins */}
        {showBusinessWarning && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-amber-500 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h3 className="font-medium text-amber-800">No Business Selected</h3>
                <p className="text-sm text-amber-700 mt-1">
                  Please select a business from the dropdown in the navigation bar
                  to view and manage projects.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        {!showBusinessWarning && (
          <ProjectFilters
            filters={filters}
            sorting={sorting}
            customers={customers}
            onFilterChange={setFilters}
            onSortingChange={setSorting}
            onClear={clearFilters}
          />
        )}

        {/* Error State */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-secondary-200 p-5 animate-pulse"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="h-5 bg-secondary-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-secondary-200 rounded w-1/2" />
                  </div>
                  <div className="h-6 bg-secondary-200 rounded w-20" />
                </div>
                <div className="h-4 bg-secondary-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && !showBusinessWarning && projects.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-secondary-200">
            <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-secondary-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-secondary-900">
              No Projects Yet
            </h3>
            <p className="text-secondary-600 mt-1 mb-6">
              {filters.status || filters.customerId || filters.search
                ? 'No projects match your filters. Try adjusting your search.'
                : 'Get started by creating your first project.'}
            </p>
            {!filters.status && !filters.customerId && !filters.search && (
              <Link to="/projects/create">
                <Button variant="primary">Create First Project</Button>
              </Link>
            )}
          </div>
        )}

        {/* Projects Grid */}
        {!isLoading && !error && projects.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        )}

        {/* Results Count */}
        {!isLoading && !error && !showBusinessWarning && projects.length > 0 && (
          <p className="text-sm text-secondary-500 text-center">
            Showing {projects.length} project{projects.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>
    </DashboardLayout>
  );
}